import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line,
  ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend,
  ComposedChart, ReferenceLine,
} from 'recharts';
import {
  BarChart3, Globe, Users, Coins, TrendingUp, AlertTriangle,
  Zap, ChevronDown, RefreshCw, LayoutDashboard, Activity,
  Server, Cpu, Database, LineChartIcon, BarChart2, Shield,
  PieChart as PieChartIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

interface PlatformOverview {
  activeWorlds: number;
  totalPlayerAccounts: number;
  cbReserveBalance: string;
  cbTotalSupply: string;
  cbCirculatingSupply: string;
  activeAnomalyFlags: number;
}

function formatUkc(raw: string | number): string {
  const n = Number(raw);
  if (isNaN(n)) return '0';
  if (n >= 1e12) return `${(n / 1e12).toFixed(3)}T`;
  if (n >= 1e9)  return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3)  return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString();
}

const TOOLTIP_STYLE: React.CSSProperties = {
  background: '#0d1526',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  fontSize: '11px',
  color: '#e2e8f0',
};

const ACCOUNT_LABELS: Record<string, string> = {
  P2E_POOL: 'P2E Pool',
  OWNER_REVENUE: 'Revenue',
  CB_ALLOCATION: 'CB Alloc',
  FUTURE_RESERVE: 'Reserve',
};
const ACCOUNT_COLORS: Record<string, string> = {
  P2E_POOL: '#10b981',
  OWNER_REVENUE: '#a855f7',
  CB_ALLOCATION: '#00d4ff',
  FUTURE_RESERVE: '#f59e0b',
};

function ChartSection({
  title,
  icon: Icon,
  color = 'text-slate-400',
  hint,
  children,
}: {
  title: string;
  icon: React.ElementType;
  color?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-4">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <p className="text-xs font-semibold text-slate-300">{title}</p>
        {hint && <span className="ml-auto text-[10px] text-slate-700">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function MetricRow({
  label,
  value,
  color = 'text-slate-300',
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div
      className="flex items-center justify-between py-2"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
    >
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}

export function GrafanaDashboardPage() {
  const role = useAuthStore((s) => s.role);
  const isPartner = role === 'WORLD_OWNER';

  const [tab, setTab] = useState<'platform' | 'world' | 'system' | 'red-metrics'>(() =>
    isPartner ? 'world' : 'platform',
  );
  const [selectedWorldId, setSelectedWorldId] = useState('');
  const [wsfChartType, setWsfChartType] = useState<'area' | 'line'>('area');
  const [txChartType, setTxChartType] = useState<'bar' | 'line'>('bar');

  /* ── Data fetching ── */

  const { data: overview, isFetching: overviewFetching, refetch } = useQuery<PlatformOverview>({
    queryKey: ['platform-overview'],
    queryFn: () => api.get('/analytics/platform/overview').then((r) => r.data),
    refetchInterval: 60_000,
    enabled: !isPartner,
  });

  const { data: worlds = [] } = useQuery<{ id: string; name: string; currencySymbol: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  /* Auto-select first world for partners (or when there's only one world) */
  useEffect(() => {
    if (!selectedWorldId && worlds.length > 0) {
      setSelectedWorldId(worlds[0].id);
    }
  }, [worlds, selectedWorldId]);

  const { data: wsfHistory = [] } = useQuery({
    queryKey: ['wsf-history', selectedWorldId],
    queryFn: () =>
      api.get(`/analytics/worlds/${selectedWorldId}/wsf-history`).then((r) => r.data).catch(() => []),
    enabled: !!selectedWorldId,
  });

  // Fallback: simple world-accounts for native bars (world-registry, always available)
  const { data: simpleAccounts = [] } = useQuery<{ accountType: string; balance: string }[]>({
    queryKey: ['world-accounts', selectedWorldId],
    queryFn: () =>
      api.get(`/worlds/${selectedWorldId}/accounts`).then((r) => r.data).catch(() => []),
    enabled: !!selectedWorldId,
  });

  // Rich: native + foreign from central-bank (adds foreign currency holdings)
  const { data: worldAllBalances } = useQuery<{
    worldId: string; worldName: string; currencySymbol: string; currencyName: string;
    nativeAccounts: { accountType: string; balance: string; currency: string }[];
    foreignBalances: { id: string; sourceWorldId: string; sourceWorldName: string; currency: string; balance: string; updatedAt: string }[];
  } | null>({
    queryKey: ['world-all-balances', selectedWorldId],
    queryFn: () =>
      api.get(`/central-bank/worlds/${selectedWorldId}/all-balances`).then((r) => r.data).catch(() => null),
    enabled: !!selectedWorldId,
  });

  const { data: txVolume = [] } = useQuery({
    queryKey: ['tx-volume', selectedWorldId],
    queryFn: () =>
      api.get(`/analytics/worlds/${selectedWorldId}/transactions`).then((r) => r.data).catch(() => []),
    enabled: !!selectedWorldId,
  });

  const { data: worldMetrics } = useQuery<Record<string, string | number> | null>({
    queryKey: ['world-metrics', selectedWorldId],
    queryFn: () =>
      api.get(`/analytics/worlds/${selectedWorldId}/metrics`).then((r) => r.data).catch(() => null),
    enabled: !!selectedWorldId,
  });

  const { data: worldAnomalies = [] } = useQuery<
    { id: string; flagType: string; severity: string; detectedAt: string; description: string }[]
  >({
    queryKey: ['world-anomalies', selectedWorldId],
    queryFn: () =>
      api.get(`/analytics/worlds/${selectedWorldId}/anomalies`).then((r) => r.data).catch(() => []),
    enabled: !!selectedWorldId,
  });

  const { data: platformAnomalies = [] } = useQuery<
    { id: string; flagType: string; severity: string; detectedAt: string }[]
  >({
    queryKey: ['platform-anomalies'],
    queryFn: () =>
      api.get('/analytics/platform/anomalies').then((r) => r.data).catch(() => []),
    enabled: !isPartner && tab === 'system',
  });

  /* ── Derived chart data ── */

  const wsfChartData = (wsfHistory as Record<string, string>[])
    .slice(0, 60)
    .reverse()
    .map((h) => ({
      t: new Date(h['calculatedAt']).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
      wsf: parseFloat(h['wsfValue'] ?? '0'),
      p2e: parseFloat(h['p2eHealthScore'] ?? '0'),
    }));

  // Use rich data when available, fall back to simple accounts so the chart never goes blank
  const nativeAccountChartData = (worldAllBalances?.nativeAccounts ?? simpleAccounts.map((acc) => ({
    accountType: acc.accountType,
    balance: acc.balance,
    currency: acc.accountType === 'CB_ALLOCATION' ? 'UKC' : (selectedWorld?.currencySymbol ?? ''),
  }))).map((acc) => ({
    name: ACCOUNT_LABELS[acc.accountType] ?? acc.accountType,
    balance: parseFloat(acc.balance ?? '0'),
    fill: ACCOUNT_COLORS[acc.accountType] ?? '#64748b',
    currency: acc.currency,
    isForeign: false,
    sourceWorldName: undefined as string | undefined,
  }));

  const foreignChartData = (worldAllBalances?.foreignBalances ?? [])
    .filter((fb) => parseFloat(fb.balance) > 0)
    .map((fb) => ({
      name: fb.currency,
      balance: parseFloat(fb.balance),
      fill: '#14b8a6',
      currency: fb.currency,
      isForeign: true,
      sourceWorldName: fb.sourceWorldName,
    }));

  const accountChartData = [...nativeAccountChartData, ...foreignChartData];
  const accountPieData = accountChartData.filter((d) => d.balance > 0);

  const txChartData = (txVolume as Record<string, string | number>[])
    .slice(0, 24)
    .reverse()
    .map((t) => ({
      t: t['bucket']
        ? new Date(t['bucket'] as string).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : '',
      events: Number(t['event_count'] ?? 0),
      volume: Number(t['total_volume'] ?? 0),
      players: Number(t['unique_players'] ?? 0),
    }));

  const platformStatCards = [
    {
      label: 'Active Worlds',
      value: overview?.activeWorlds ?? 0,
      icon: Globe,
      color: 'text-cyan-400',
      border: 'rgba(0,212,255,0.2)',
    },
    {
      label: 'Player Accounts',
      value: (overview?.totalPlayerAccounts ?? 0).toLocaleString(),
      icon: Users,
      color: 'text-purple-400',
      border: 'rgba(124,58,237,0.2)',
    },
    {
      label: 'Circulating',
      value: `${formatUkc(overview?.cbCirculatingSupply ?? '0')} UKC`,
      icon: Coins,
      color: 'text-emerald-400',
      border: 'rgba(16,185,129,0.2)',
    },
    {
      label: 'Total Supply',
      value: `${formatUkc(overview?.cbTotalSupply ?? '0')} UKC`,
      icon: TrendingUp,
      color: 'text-amber-400',
      border: 'rgba(245,158,11,0.2)',
    },
    {
      label: 'Anomaly Flags',
      value: overview?.activeAnomalyFlags ?? 0,
      icon: AlertTriangle,
      color: overview?.activeAnomalyFlags ? 'text-red-400' : 'text-green-400',
      border: overview?.activeAnomalyFlags ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
    },
  ];

  const availableTabs = [
    ...(!isPartner ? [{ key: 'platform' as const, label: 'Platform Overview', icon: LayoutDashboard }] : []),
    { key: 'world' as const, label: isPartner ? 'My Worlds Analytics' : 'World Analytics', icon: Activity },
    ...(!isPartner ? [{ key: 'system' as const, label: 'System Metrics', icon: Server }] : []),
    ...(!isPartner ? [{ key: 'red-metrics' as const, label: 'RED Metrics', icon: Cpu }] : []),
  ];

  const selectedWorld = worlds.find((w) => w.id === selectedWorldId);

  return (
    <div className="p-8 space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/15 to-blue-500/15 border border-purple-500/20 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Dashboards</h2>
            <p className="text-xs text-slate-600">
              {isPartner ? 'Live analytics for your worlds' : 'Live platform and world analytics'}
            </p>
          </div>
        </div>
        {!isPartner && (
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:border-cyan-500/20 bg-white/[0.02]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${overviewFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/5 rounded-xl w-fit">
        {availableTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg transition-all ${
              tab === key
                ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/25 text-white'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          TAB: Platform Overview  (admin only)
      ══════════════════════════════════════════ */}
      {tab === 'platform' && !isPartner && (
        <motion.div key="platform" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {platformStatCards.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass-card p-4"
                  style={{ borderColor: s.border }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                    <p className="text-[10px] font-medium uppercase tracking-widest text-slate-600">{s.label}</p>
                  </div>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Supply distribution pie + platform health */}
          {overview && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="glass-card p-5">
                <div className="flex items-center gap-2 mb-4">
                  <PieChartIcon className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-white">Supply Distribution</h3>
                </div>
                {(() => {
                  const circulating = parseFloat(overview.cbCirculatingSupply);
                  const reserve = parseFloat(overview.cbReserveBalance);
                  const total = parseFloat(overview.cbTotalSupply);
                  const unissued = Math.max(0, total - circulating - reserve);
                  const supplyPie = [
                    { name: 'Circulating', value: circulating, fill: '#10b981' },
                    { name: 'CB Reserve',  value: reserve,     fill: '#00d4ff' },
                    { name: 'Unissued',    value: unissued,    fill: '#334155' },
                  ].filter((d) => d.value > 0);
                  return (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={supplyPie}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {supplyPie.map((entry, idx) => (
                            <Cell key={idx} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          formatter={(v: number) => [formatUkc(v) + ' UKC', '']}
                        />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>

              <div className="glass-card p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-sm font-semibold text-white">Platform Health</h3>
                </div>
                <div className="space-y-1">
                  <MetricRow label="Total Supply"       value={formatUkc(overview.cbTotalSupply) + ' UKC'}       color="text-amber-400" />
                  <MetricRow label="Circulating Supply" value={formatUkc(overview.cbCirculatingSupply) + ' UKC'} color="text-emerald-400" />
                  <MetricRow label="CB Reserve Balance" value={formatUkc(overview.cbReserveBalance) + ' UKC'}    color="text-cyan-400" />
                  <MetricRow
                    label="Reserve Ratio"
                    value={(() => {
                      const r = parseFloat(overview.cbReserveBalance);
                      const c = parseFloat(overview.cbCirculatingSupply);
                      return c > 0 ? (r / c * 100).toFixed(1) + '%' : '—';
                    })()}
                    color="text-purple-400"
                  />
                  <MetricRow label="Active Worlds"   value={overview.activeWorlds}                         color="text-cyan-300" />
                  <MetricRow label="Player Accounts" value={overview.totalPlayerAccounts.toLocaleString()} color="text-purple-300" />
                  <MetricRow
                    label="Open Anomalies"
                    value={overview.activeAnomalyFlags}
                    color={overview.activeAnomalyFlags > 0 ? 'text-red-400' : 'text-emerald-400'}
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ══════════════════════════════════════════
          TAB: World Analytics  (all roles)
      ══════════════════════════════════════════ */}
      {tab === 'world' && (
        <motion.div key="world" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="glass-card overflow-hidden">
            <div className="h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-semibold text-white">
                  {selectedWorld ? selectedWorld.name : 'World Analytics'}
                </h3>
                {selectedWorld && (
                  <span className="font-mono text-[10px] text-slate-600 bg-white/[0.03] border border-white/5 px-2 py-0.5 rounded">
                    {selectedWorld.currencySymbol}
                  </span>
                )}
              </div>

              {/* World selector — only show dropdown when there's a real choice */}
              {(worlds.length > 1 || !isPartner) && (
                <div className="relative">
                  <select
                    value={selectedWorldId}
                    onChange={(e) => setSelectedWorldId(e.target.value)}
                    className="text-xs bg-[#0b1120] border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500/40 appearance-none pr-7"
                  >
                    {!isPartner && <option value="">Select a world…</option>}
                    {worlds.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} ({w.currencySymbol})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600 pointer-events-none" />
                </div>
              )}
            </div>

            {!selectedWorldId ? (
              <div className="px-5 py-14 text-center text-slate-700 text-xs">
                Select a world above to view its analytics charts
              </div>
            ) : (
              <div className="p-5 space-y-8">

                {/* ── WSF explainer ── */}
                <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <p className="text-sm font-semibold text-amber-300">World Scaling Factor (WSF)</p>
                    {worldMetrics && (() => {
                      const v = Number(worldMetrics['wsfValue'] ?? 1);
                      const { label, cls } =
                        v >= 1.5 ? { label: 'BOOSTED',     cls: 'bg-emerald-500/20 text-emerald-300' } :
                        v >= 1.1 ? { label: 'EXPANDING',   cls: 'bg-cyan-500/20 text-cyan-300'      } :
                        v >= 0.9 ? { label: 'NEUTRAL',     cls: 'bg-amber-500/20 text-amber-300'    } :
                        v >= 0.5 ? { label: 'CONTRACTING', cls: 'bg-orange-500/20 text-orange-300'  } :
                                   { label: 'CRITICAL',    cls: 'bg-red-500/20 text-red-300'        };
                      return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
                    })()}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    WSF is a live multiplier (0.0 – 2.0) that controls how generously this world rewards players with UKC.
                    <strong className="text-slate-300"> 1.0 = neutral baseline</strong> — rewards paid at the standard rate.
                    Values above 1 boost earnings; below 1 they are reduced to protect the P2E pool.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">Revenue Velocity (60% weight)</p>
                      <p className="text-xs text-slate-300">How fast revenue is arriving vs. the world's 30-day average. Rising sales push WSF up; slow periods pull it down.</p>
                    </div>
                    <div className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">P2E Health Score (40% weight)</p>
                      <p className="text-xs text-slate-300">P2E pool balance ÷ target reserve, capped at 1.0. A full pool keeps rewards flowing; a depleted pool forces WSF lower.</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-600 italic">
                    All three values showing 1.0 is expected for a new world with no transaction history — both
                    velocity inputs are zero so the system defaults to 1.0 neutral. WSF will diverge once real
                    player activity generates REVENUE_SPLIT events.
                  </p>
                </div>

                {/* World financial metrics row */}
                {worldMetrics && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'World Scaling Factor', key: 'wsfValue',            color: 'text-amber-400',   fmt: (v: string) => Number(v).toFixed(4) },
                      { label: 'P2E Health Score',      key: 'p2eHealthScore',      color: 'text-emerald-400', fmt: (v: string) => Number(v).toFixed(3) },
                      { label: 'Total Balance',         key: 'totalBalance',        color: 'text-cyan-400',    fmt: (v: string) => formatUkc(v) },
                      { label: 'Revenue Velocity',      key: 'rewardVelocityScore', color: 'text-purple-400',  fmt: (v: string) => Number(v).toFixed(3) },
                    ].map(({ label, key, color, fmt }) => {
                      const raw = worldMetrics[key] as string | undefined;
                      if (raw == null) return null;
                      return (
                        <div key={key} className="bg-white/[0.02] border border-white/5 rounded-xl px-3 py-3">
                          <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">{label}</p>
                          <p className={`text-lg font-bold ${color}`}>{fmt(raw)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* WSF trend + Account balances */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* WSF history with chart type toggle */}
                  <ChartSection
                    title="World Scaling Factor — history"
                    icon={Zap}
                    color="text-amber-400"
                    hint="1.0 = neutral · >1.0 = boosted · <1.0 = reduced"
                  >
                    <div className="flex gap-1 mb-3">
                      {(['area', 'line'] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setWsfChartType(t)}
                          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-all ${
                            wsfChartType === t
                              ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                              : 'border-white/5 text-slate-600 hover:text-slate-400'
                          }`}
                        >
                          {t === 'area' ? <BarChart2 className="w-3 h-3" /> : <LineChartIcon className="w-3 h-3" />}
                          {t}
                        </button>
                      ))}
                    </div>
                    {wsfChartData.length === 0 ? (
                      <div className="h-44 flex items-center justify-center text-slate-700 text-xs rounded-xl border border-white/5">
                        No WSF history recorded yet
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        {wsfChartType === 'area' ? (
                          <AreaChart data={wsfChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                            <defs>
                              <linearGradient id="wsfGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="p2eGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                            <XAxis dataKey="t" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                            <YAxis tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} domain={[0, 2.5]} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} />
                            <ReferenceLine y={1.0} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />
                            <Area type="monotone" dataKey="wsf" stroke="#f59e0b" strokeWidth={1.5} fill="url(#wsfGrad)" name="WSF" dot={false} />
                            <Area type="monotone" dataKey="p2e" stroke="#10b981" strokeWidth={1.5} fill="url(#p2eGrad)" name="P2E Health" dot={false} />
                          </AreaChart>
                        ) : (
                          <LineChart data={wsfChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                            <XAxis dataKey="t" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                            <YAxis tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} domain={[0, 2.5]} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} />
                            <ReferenceLine y={1.0} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />
                            <Line type="monotone" dataKey="wsf" stroke="#f59e0b" strokeWidth={2} name="WSF" dot={false} />
                            <Line type="monotone" dataKey="p2e" stroke="#10b981" strokeWidth={1.5} name="P2E Health" dot={false} strokeDasharray="4 2" />
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    )}
                  </ChartSection>

                  {/* Account balances bar chart + legend strip */}
                  <ChartSection title="Account Balances" icon={Coins} color="text-emerald-400">
                    {accountChartData.length === 0 ? (
                      <div className="h-44 flex items-center justify-center text-slate-700 text-xs rounded-xl border border-white/5">
                        No balance data available
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={accountChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={(v) => formatUkc(v)} />
                            <Tooltip
                              contentStyle={TOOLTIP_STYLE}
                              formatter={(v: number, _name: string, props: { payload?: { currency?: string; sourceWorldName?: string } }) => [
                                `${formatUkc(v)} ${props.payload?.currency ?? ''}`,
                                props.payload?.sourceWorldName ?? 'Balance',
                              ]}
                            />
                            <Bar dataKey="balance" radius={[4, 4, 0, 0]}>
                              {accountChartData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                        {accountPieData.length > 0 && (
                          <div className="grid grid-cols-2 gap-1.5">
                            {accountChartData.map((acc) => (
                              <div
                                key={acc.name}
                                className={`flex items-center justify-between bg-white/[0.02] rounded-lg px-2.5 py-1.5 ${
                                  acc.isForeign ? 'border border-teal-500/15' : 'border border-white/5'
                                }`}
                              >
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ background: acc.fill }}
                                  />
                                  <span className="text-[10px] text-slate-500 truncate">{acc.name}</span>
                                </div>
                                <div className="flex items-center gap-1 ml-1 shrink-0">
                                  <span className="font-mono text-[9px] text-slate-600">{acc.currency}</span>
                                  <span className="font-mono text-[10px] font-semibold text-slate-300">
                                    {formatUkc(acc.balance)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </ChartSection>
                </div>

                {/* Transaction volume */}
                {txChartData.length > 0 && (
                  <ChartSection
                    title="Transaction Events — last 24h"
                    icon={TrendingUp}
                    color="text-cyan-400"
                    hint="hourly buckets"
                  >
                    <div className="flex gap-1 mb-3">
                      {(['bar', 'line'] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setTxChartType(t)}
                          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-all ${
                            txChartType === t
                              ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                              : 'border-white/5 text-slate-600 hover:text-slate-400'
                          }`}
                        >
                          {t === 'bar' ? <BarChart2 className="w-3 h-3" /> : <LineChartIcon className="w-3 h-3" />}
                          {t}
                        </button>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      {txChartType === 'bar' ? (
                        <BarChart data={txChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                          <XAxis dataKey="t" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Bar dataKey="events" fill="#00d4ff" opacity={0.75} radius={[3, 3, 0, 0]} name="Events" />
                        </BarChart>
                      ) : (
                        <ComposedChart data={txChartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="t" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                          <YAxis yAxisId="left"  tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#475569', fontSize: 9 }} tickLine={false} axisLine={false} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Line yAxisId="left"  type="monotone" dataKey="events"  stroke="#00d4ff" strokeWidth={2}   dot={false} name="Events" />
                          <Line yAxisId="right" type="monotone" dataKey="players" stroke="#a855f7" strokeWidth={1.5} dot={false} name="Players" strokeDasharray="4 2" />
                        </ComposedChart>
                      )}
                    </ResponsiveContainer>
                  </ChartSection>
                )}

                {/* Active anomaly flags for this world */}
                {worldAnomalies.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      <p className="text-xs font-semibold text-slate-300">
                        Active Anomaly Flags ({worldAnomalies.length})
                      </p>
                    </div>
                    <div className="space-y-2">
                      {worldAnomalies.slice(0, 5).map((a) => (
                        <div
                          key={a.id}
                          className="flex items-center gap-3 bg-red-500/5 border border-red-500/15 rounded-xl px-3 py-2.5"
                        >
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              a.severity === 'CRITICAL'
                                ? 'bg-red-500/20 text-red-400'
                                : a.severity === 'HIGH'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }`}
                          >
                            {a.severity}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-300 truncate">{a.flagType.replace(/_/g, ' ')}</p>
                            {a.description && (
                              <p className="text-[10px] text-slate-600 truncate">{a.description}</p>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-700 flex-shrink-0">
                            {new Date(a.detectedAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ══════════════════════════════════════════
          TAB: System Metrics  (admin only)
      ══════════════════════════════════════════ */}
      {tab === 'system' && !isPartner && (
        <motion.div key="system" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">

          {/* Microservice health grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'API Gateway',    port: 7501, icon: Server,    color: 'text-cyan-400',   border: 'rgba(0,212,255,0.15)'   },
              { name: 'Ledger Service', port: 7503, icon: Database,  color: 'text-purple-400', border: 'rgba(124,58,237,0.15)'  },
              { name: 'Central Bank',   port: 7504, icon: Cpu,       color: 'text-emerald-400',border: 'rgba(16,185,129,0.15)'  },
              { name: 'World Registry', port: 7505, icon: Globe,     color: 'text-amber-400',  border: 'rgba(245,158,11,0.15)'  },
              { name: 'Analytics',      port: 7509, icon: BarChart3, color: 'text-blue-400',   border: 'rgba(59,130,246,0.15)'  },
              { name: 'Reporting',      port: 7510, icon: Activity,  color: 'text-pink-400',   border: 'rgba(236,72,153,0.15)'  },
            ].map((svc) => {
              const Icon = svc.icon;
              return (
                <div
                  key={svc.name}
                  className="glass-card p-4 flex items-center gap-3"
                  style={{ borderColor: svc.border }}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/[0.03] border border-white/5">
                    <Icon className={`w-4 h-4 ${svc.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-slate-300">{svc.name}</div>
                    <div className="font-mono text-[10px] text-slate-600">:{svc.port}</div>
                  </div>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                </div>
              );
            })}
          </div>

          {/* CB reserve snapshot */}
          {overview && (
            <div className="glass-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Coins className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-white">Central Bank Reserve Snapshot</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                {[
                  { label: 'Reserve Balance', value: formatUkc(overview.cbReserveBalance) + ' UKC', color: 'text-cyan-400' },
                  { label: 'Circulating',     value: formatUkc(overview.cbCirculatingSupply) + ' UKC', color: 'text-emerald-400' },
                  { label: 'Total Supply',    value: formatUkc(overview.cbTotalSupply) + ' UKC', color: 'text-amber-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white/[0.02] border border-white/5 rounded-xl px-4 py-3">
                    <p className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">{label}</p>
                    <p className={`text-lg font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Platform anomaly feed */}
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <h3 className="text-sm font-semibold text-white">Platform Anomaly Feed</h3>
              <span className="ml-auto text-xs text-slate-600">{platformAnomalies.length} open</span>
            </div>
            {platformAnomalies.length === 0 ? (
              <div className="text-center py-8 text-slate-700 text-xs">
                <Shield className="w-8 h-8 mx-auto mb-2 text-emerald-400/30" />
                No open anomaly flags across the platform
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {platformAnomalies.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-3 bg-white/[0.02] border border-white/5 rounded-xl px-3 py-2.5"
                  >
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        a.severity === 'CRITICAL'
                          ? 'bg-red-500/20 text-red-400'
                          : a.severity === 'HIGH'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}
                    >
                      {a.severity}
                    </span>
                    <span className="text-xs text-slate-400 flex-1 truncate">
                      {a.flagType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-slate-700 flex-shrink-0">
                      {new Date(a.detectedAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ══════════════════════════════════════════
          TAB: RED Metrics (admin only)
      ══════════════════════════════════════════ */}
      {tab === 'red-metrics' && !isPartner && (
        <motion.div key="red-metrics" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">

          {/* Explanation banner */}
          <div className="glass-card p-5 border-l-2 border-cyan-500/40">
            <div className="flex items-start gap-3">
              <Cpu className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-400 leading-relaxed space-y-1">
                <p>
                  <span className="text-white font-semibold">RED Metrics</span> — Rate, Errors and Duration —
                  are captured per-service via Prometheus and visualised in the{' '}
                  <span className="text-cyan-300 font-semibold">UKFMS Service Metrics</span> Grafana dashboard.
                  Circuit breaker state (<code className="text-[10px] font-mono text-cyan-300 px-1 bg-cyan-400/10 rounded">ukfms_circuit_breaker_state</code>) and
                  DLQ accumulation (<code className="text-[10px] font-mono text-red-300 px-1 bg-red-400/10 rounded">ukfms_dlq_messages_total</code>) are also tracked there.
                </p>
                <p className="text-slate-600">Metrics endpoint: <code className="text-[10px] font-mono text-slate-500">GET /metrics</code> on each service (Prometheus format)</p>
              </div>
            </div>
          </div>

          {/* Dashboard deep-links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: 'Service RED Metrics',
                desc: 'Request rate, error rate, p50/p95/p99 latency, top routes, event loop, heap — per service',
                dashboardId: 'ukfms-service-metrics',
                color: 'from-cyan-600/80 to-purple-700/80',
                hoverColor: 'hover:from-cyan-500/80 hover:to-purple-600/80',
                badge: 'NEW · P3',
              },
              {
                title: 'Platform Health',
                desc: '24 h transaction volume, active worlds, circulating supply — TimescaleDB-backed',
                dashboardId: 'ukfms-platform-health',
                color: 'from-emerald-600/80 to-cyan-700/80',
                hoverColor: 'hover:from-emerald-500/80 hover:to-cyan-600/80',
                badge: 'Platform',
              },
            ].map((d) => {
              const grafanaBase = `${window.location.protocol}//${window.location.hostname}:7561`;
              const url = `${grafanaBase}/d/${d.dashboardId}?orgId=1&refresh=30s`;
              return (
                <div key={d.dashboardId} className="glass-card p-5 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-slate-200">{d.title}</p>
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-400 border border-cyan-400/20">
                          {d.badge}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{d.desc}</p>
                    </div>
                  </div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r ${d.color} ${d.hoverColor} border border-white/[0.08] transition-all shadow-lg`}
                  >
                    <LineChartIcon className="w-3.5 h-3.5" />
                    Open in Grafana
                  </a>
                </div>
              );
            })}
          </div>

          {/* Metric reference table */}
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-white">Prometheus Metric Reference</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {['Metric', 'Type', 'Labels', 'Description'].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-600 tracking-widest uppercase whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['ukfms_http_requests_total',        'counter',   'service, method, route, status_code', 'Total HTTP requests — used for rate and error rate'],
                    ['ukfms_http_request_duration_ms',   'histogram', 'service, method, route',              'Request duration in ms — used for p50/p95/p99 latency'],
                    ['ukfms_circuit_breaker_state',      'gauge',     'service, circuit',                    '0=closed, 0.5=half-open, 1=open'],
                    ['ukfms_dlq_messages_total',         'counter',   'topic',                               'Dead-letter queue message accumulation'],
                    ['ukfms_kafka_consumer_lag',         'gauge',     'topic, partition, group',             'Kafka consumer group partition lag'],
                  ].map(([metric, type_, labels, desc]) => (
                    <tr key={metric} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="px-3 py-2.5 font-mono text-cyan-300 text-[11px] whitespace-nowrap">{metric}</td>
                      <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{type_}</td>
                      <td className="px-3 py-2.5 font-mono text-slate-600 text-[10px]">{labels}</td>
                      <td className="px-3 py-2.5 text-slate-500">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
